import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { SERVER_API_URL } from '../../app.constants';
import { Pageable } from '../model/pageable';
import { createRequestOption } from '../model/request-util';

export interface OauthClientDTO {
    id?: number | undefined;
    clientId: string;
    redirectUri: string;
    description: string;
}

@Injectable({
    providedIn: 'root'
})
export class OauthClientsService {

    private readonly baseUrl = SERVER_API_URL + 'oauth-clients';

    constructor(private http: HttpClient) {}

    /**
     * GET /oauth-clients
     * Returns a paginated list of all registered OAuth clients.
     */
    getAll(pagination?: Pageable): Observable<HttpResponse<OauthClientDTO[]>> {
        const params = createRequestOption(pagination);
        return this.http.get<OauthClientDTO[]>(this.baseUrl, { params, observe: 'response' });
    }

    /**
     * GET /oauth-clients/{id}
     * Retrieves a single OAuth client by its unique identifier.
     */
    getById(id: number): Observable<OauthClientDTO> {
        return this.http.get<OauthClientDTO>(`${this.baseUrl}/${id}`);
    }

    /**
     * GET /oauth-clients/search?clientName=xxx
     * Looks up an OAuth client using its client name.
     */
    findByClientName(clientName: string): Observable<OauthClientDTO> {
        const params = new HttpParams().set('clientName', clientName);
        return this.http.get<OauthClientDTO>(`${this.baseUrl}/search`, { params });
    }

    /**
     * POST /oauth-clients
     * Registers a new OAuth client.
     */
    create(oauthClient: OauthClientDTO): Observable<OauthClientDTO> {
        return this.http.post<OauthClientDTO>(this.baseUrl, oauthClient);
    }

    /**
     * PUT /oauth-clients/{id}
     * Updates the OAuth client identified by the given ID.
     */
    update(id: number, oauthClient: OauthClientDTO): Observable<OauthClientDTO> {
        return this.http.put<OauthClientDTO>(`${this.baseUrl}/${id}`, oauthClient);
    }

    /**
     * DELETE /oauth-clients/{id}
     * Deletes the OAuth client identified by the given ID.
     */
    delete(id: number): Observable<void> {
        return this.http.delete<void>(`${this.baseUrl}/${id}`);
    }
}
